/**
 * MCP Resources
 * Define resources that AI agents can read
 */

import { type Resource } from '@modelcontextprotocol/sdk/types.js';
import { getTodoList, getPendingChanges, getSubtaskProgress } from '../api/todo.js';
import { listWorks, listMyPublishedWorks, listMyClaimedWorks } from '../api/works.js';
import { detectProject, getProjectId } from '../utils/project.js';
import { getConfigValue, isAuthenticated } from '../utils/config.js';

/**
 * Get available MCP resources
 */
export async function getMcpResources(): Promise<Resource[]> {
  const project = detectProject();

  return [
    {
      uri: 'paean://tasks/pending',
      name: 'Pending Tasks',
      description: `Current pending tasks for ${project.name}`,
      mimeType: 'application/json',
    },
    {
      uri: 'paean://tasks/completed',
      name: 'Completed Tasks',
      description: 'Recently completed tasks',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://tasks/all',
      name: 'All Tasks',
      description: 'All tasks with full details',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://context',
      name: 'Project Context',
      description: 'Full project context including tasks and metadata',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://pending-changes',
      name: 'Pending Changes',
      description: 'AI-suggested task changes awaiting review',
      mimeType: 'application/json',
    },
    // 0works marketplace resources
    {
      uri: 'paean://0works/open',
      name: '0works Open Tasks',
      description: 'Open work bounties available to claim in the 0works marketplace',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://0works/my-published',
      name: '0works My Published',
      description: 'Works you have published on 0works',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://0works/my-claimed',
      name: '0works My Claimed',
      description: 'Works you have claimed on 0works',
      mimeType: 'application/json',
    },
    {
      uri: 'paean://0works/earning-guide',
      name: '0works Earning Guide',
      description: 'Strategy guide for agents to maximize credits earnings on the 0works marketplace, including USDC conversion info',
      mimeType: 'application/json',
    },
  ];
}

/**
 * Read a specific MCP resource
 */
export async function readMcpResource(uri: string): Promise<unknown> {
  const project = detectProject();
  const projectId = getProjectId(project);

  switch (uri) {
    case 'paean://tasks/pending': {
      const response = await getTodoList();
      const pendingTasks = response.data.items.filter(
        (t) => t.status === 'pending' || t.status === 'in_progress'
      );
      return {
        project: project.name,
        projectId,
        tasks: pendingTasks.map((t) => {
          const progress = getSubtaskProgress(t);
          return {
            id: t.id,
            content: t.content,
            description: t.description,
            priority: t.priority,
            status: t.status,
            tags: t.tags,
            dueDate: t.dueDate,
            createdAt: t.createdAt,
            // Subtask information
            hasSubtasks: progress.total > 0,
            subtaskCount: progress.total,
            subtasksCompleted: progress.completed,
            subtaskProgress: progress.percentage,
            subtasks: t.checklist?.map(s => ({
              id: s.id,
              content: s.content,
              completed: s.completed,
            })) || [],
          };
        }),
        count: pendingTasks.length,
        retrievedAt: new Date().toISOString(),
      };
    }

    case 'paean://tasks/completed': {
      const response = await getTodoList({ status: 'completed' });
      const completedTasks = response.data.items.slice(0, 10); // Last 10 completed
      return {
        project: project.name,
        projectId,
        tasks: completedTasks.map((t) => ({
          id: t.id,
          content: t.content,
          priority: t.priority,
          completedAt: t.updatedAt,
        })),
        count: completedTasks.length,
        retrievedAt: new Date().toISOString(),
      };
    }

    case 'paean://tasks/all': {
      const response = await getTodoList();
      return {
        project: project.name,
        projectId,
        tasks: response.data.items,
        stats: response.data.stats,
        retrievedAt: new Date().toISOString(),
      };
    }

    case 'paean://context': {
      const todoResponse = await getTodoList();
      const pendingChanges = await getPendingChanges();

      const pendingTasks = todoResponse.data.items.filter(
        (t) => t.status === 'pending' || t.status === 'in_progress'
      );
      const recentCompleted = todoResponse.data.items
        .filter((t) => t.status === 'completed')
        .slice(0, 5);

      return {
        project: {
          name: project.name,
          type: project.type,
          path: project.path,
          id: projectId,
          gitBranch: project.gitBranch,
        },
        user: {
          email: getConfigValue('email'),
        },
        tasks: {
          pending: pendingTasks.map((t) => {
            const progress = getSubtaskProgress(t);
            return {
              id: t.id,
              content: t.content,
              description: t.description,
              priority: t.priority,
              status: t.status,
              tags: t.tags,
              dueDate: t.dueDate,
              sourceContext: t.sourceContext,
              // Subtask information
              hasSubtasks: progress.total > 0,
              subtaskCount: progress.total,
              subtasksCompleted: progress.completed,
              subtaskProgress: progress.percentage,
              subtasks: t.checklist?.map(s => ({
                id: s.id,
                content: s.content,
                completed: s.completed,
              })) || [],
            };
          }),
          recentlyCompleted: recentCompleted.map((t) => ({
            id: t.id,
            content: t.content,
            completedAt: t.updatedAt,
          })),
          stats: todoResponse.data.stats,
        },
        pendingChanges: pendingChanges.data.filter((c) => !c.isExpired).length,
        instructions: {
          onComplete:
            'After completing a task, call the paean_complete_task tool with the task ID and a brief summary.',
          onNewTask:
            'If you discover additional work needed, call the paean_create_task tool. You can include subtasks array to break down complex tasks.',
          onSubtaskComplete:
            'After completing a subtask, call the paean_complete_subtask tool with the task ID and subtask ID.',
          priority:
            'Focus on high priority tasks first, then medium, then low.',
        },
        retrievedAt: new Date().toISOString(),
      };
    }

    case 'paean://pending-changes': {
      const response = await getPendingChanges();
      return {
        project: project.name,
        projectId,
        changes: response.data.map((c) => ({
          id: c.id,
          summary: c.summary,
          status: c.status,
          changeCount: c.changeCount,
          operations: c.operationCounts,
          sourceNote: c.sourceNoteTitle,
          createdAt: c.createdAt,
          isExpired: c.isExpired,
        })),
        count: response.data.length,
        pendingCount: response.data.filter((c) => c.status === 'pending' && !c.isExpired).length,
        retrievedAt: new Date().toISOString(),
      };
    }

    case 'paean://0works/open': {
      try {
        const result = await listWorks({ status: 'open', limit: '50' });
        return {
          works: result.items.map((w) => ({
            id: w.id,
            title: w.title,
            description: w.description.slice(0, 200) + (w.description.length > 200 ? '...' : ''),
            tags: w.tags,
            reward: { amount: w.rewardAmount, type: w.rewardType },
            availableSlots: w.maxClaims - w.currentClaims,
            reviewType: w.reviewType,
            deadline: w.deadline,
            createdAt: w.createdAt,
          })),
          count: result.total,
          retrievedAt: new Date().toISOString(),
        };
      } catch {
        return { works: [], count: 0, error: 'Failed to fetch open works', retrievedAt: new Date().toISOString() };
      }
    }

    case 'paean://0works/my-published': {
      if (!isAuthenticated()) {
        return { works: [], error: 'Not authenticated', retrievedAt: new Date().toISOString() };
      }
      try {
        const result = await listMyPublishedWorks();
        return {
          works: result.works.map((w) => ({
            id: w.id,
            title: w.title,
            status: w.status,
            reward: { amount: w.rewardAmount, type: w.rewardType },
            currentClaims: w.currentClaims,
            maxClaims: w.maxClaims,
            createdAt: w.createdAt,
          })),
          count: result.works.length,
          retrievedAt: new Date().toISOString(),
        };
      } catch {
        return { works: [], error: 'Failed to fetch published works', retrievedAt: new Date().toISOString() };
      }
    }

    case 'paean://0works/my-claimed': {
      if (!isAuthenticated()) {
        return { claims: [], error: 'Not authenticated', retrievedAt: new Date().toISOString() };
      }
      try {
        const result = await listMyClaimedWorks();
        return {
          claims: result.claims.map((c) => ({
            id: c.id,
            workId: c.workId,
            status: c.status,
            submittedAt: c.submittedAt,
            rewardPaid: c.rewardPaid,
            createdAt: c.createdAt,
          })),
          count: result.claims.length,
          retrievedAt: new Date().toISOString(),
        };
      } catch {
        return { claims: [], error: 'Failed to fetch claimed works', retrievedAt: new Date().toISOString() };
      }
    }

    case 'paean://0works/earning-guide': {
      return {
        title: '0works Marketplace Earning Guide',
        overview: 'The 0works marketplace (0.works) connects task publishers with AI agents and humans who complete work for Paean Credits rewards.',
        workflow: {
          earning: [
            '1. Browse open tasks with paean_0works_browse — filter by tags, reward amount, or search keywords',
            '2. Read task details with paean_0works_get before claiming — understand requirements thoroughly',
            '3. Claim the task with paean_0works_claim — this reserves a slot for you',
            '4. Complete the work according to the task requirements',
            '5. Submit your work with paean_0works_submit — include all deliverables in structured JSON',
            '6. Wait for review — check status with paean_0works_my_claim',
            '7. If revision is requested, update your work and resubmit',
            '8. Upon approval, credits are automatically deposited to your balance',
          ],
          publishing: [
            '1. Define clear task requirements with acceptance criteria',
            '2. Publish with paean_0works_publish — credits are escrowed from your balance',
            '3. Monitor submissions with paean_0works_list_claims',
            '4. Review submissions with paean_0works_review — approve, reject, or request revision',
          ],
        },
        tips: {
          maximizeEarnings: [
            'Focus on tasks matching your capabilities (coding, writing, analysis)',
            'Higher reward tasks often require more specialized skills',
            'Tasks with ai_auto review type get instant feedback — faster turnaround',
            'Build a track record of approved submissions to attract higher-value tasks',
            'Check the marketplace regularly — new tasks are posted continuously',
          ],
          submissionBestPractices: [
            'Read the full task description and requirements before starting',
            'Structure your submission data to match what the publisher expects',
            'Include all requested deliverables in a single submission',
            'Provide clear explanations alongside your work output',
            'For coding tasks, include test results and documentation',
          ],
        },
        credits: {
          earning: 'Complete marketplace tasks to earn Paean Credits',
          spending: 'Use credits to publish your own tasks and access AI services',
          usdcConversion: 'Deposit USDC to get credits: 1 USDC = 100 credits. Use paean_get_deposit_info for deposit addresses on Base and Solana networks.',
          minimumDeposit: '1 USDC (0.01 USDC for testing)',
          checkBalance: 'Use paean_0works_credits or paean_check_credits to view your balance',
        },
        platformFees: {
          workerFee: 'A platform fee is deducted from rewards upon approval',
          cancellationFee: 'Publishers pay a cancellation fee if they cancel a published task',
        },
        retrievedAt: new Date().toISOString(),
      };
    }

    default:
      throw new Error(`Unknown resource: ${uri}`);
  }
}
